import { Signal } from "../core/Signal.js";
import { IPoint, Point } from "../geom/Point.js";
import { Transform } from "../geom/Transform.js";
import { TransformMode } from "../geom/transformmode/TransformMode.js";
import { Calc } from "../math/Calc.js";
import { object } from "../util/ObjectUtil.js";
import { Actor, ActorNonOptions, ActorOptions, Pointer } from "./Actor.js";

export type SpinOrigin = {
    x?: number | string;
    y?: number | string;
} | "auto";

export interface SpinOptions extends ActorOptions {
    origin?: SpinOrigin;
    maxRadius?: number;
    minRadius?: number;
    maxAngle?: number;
    minAngle?: number;
}

export class Spin extends Actor<Pointer, SpinOptions> implements SpinOptions {
    public static DEFAULT: SpinOptions = {
        maxPointers: 1,
        origin: "auto",
        maxRadius: Number.MAX_VALUE,
        minRadius: 0,
        maxAngle: Number.MAX_VALUE,
        minAngle: -Number.MAX_VALUE
    };

    public onSpin = Signal.create<[number]>();
    public onChangeAngle = Signal.create<[number]>();

    public origin: SpinOrigin;
    public minRadius: number;
    public maxRadius: number;
    public minAngle: number;
    public maxAngle: number;

    private _transform: Transform = new Transform();
    private _target: HTMLElement = null;
    private _angle: number = 0;
    private _angleDelta: number = 0;
    private _newAngle: number = null;

    constructor(target: HTMLElement = null, options: SpinOptions = null) {
        super(null, ActorNonOptions);

        if (options !== ActorNonOptions)
            this.setOptions(options);

        this.target = target;
        this._transform.mode = TransformMode.Inject;

        var isInRadius = true;
        var start = () => {
            if (!this.isInRadius())
                return;
            this._newAngle = this.calcAngle();
            this._angleDelta = this.angle - this._newAngle;
        };

        this.onDown.add(start);

        this.onMove.add(() => {
            if (!this.isInRadius()) {
                isInRadius = false;
                return;
            }
            if (!isInRadius) {
                start();
                isInRadius = true;
            }
            var before = this.angle;
            this.angle = this.calcAngle() + this._angleDelta;
            if (this.angle != before)
                this.onSpin.dispatch(this.angle);
        });
    }

    public setOptions(options: SpinOptions) {
        options = object.merge({}, Spin.DEFAULT, options);
        this.origin = options.origin;
        this.maxRadius = options.maxRadius;
        this.minRadius = options.minRadius;
        this.minAngle = options.minAngle;
        this.maxAngle = options.maxAngle;
        super.setOptions(options);
    }

    public get angle(): number {
        var a = this._angle % 360;
        if (a < 0)
            a += 360;
        return a;
    }

    public set angle(angle: number) {
        angle %= 360;
        if (angle < 0)
            angle += 360;
        angle = Calc.clampAngle(this._angle, angle, this.minAngle, this.maxAngle);
        var delta = Calc.shortRotation(this._angle, angle);
        if (this._angle === angle)
            return;

        this._angle = angle;
        if (this.target) {
            var matrix = this._transform.matrix;
            if (this.origin === "auto") {
                matrix.appendRotation(0, 0, delta);
                this.target.style.transform = matrix.getCss();
            }
            else {
                var origin = this.getOrigin();
                var localX: number = origin.x - this._transform.origin.x;
                var localY: number = origin.y - this._transform.origin.y;
                matrix.appendPosition(localX, localY);
                matrix.appendRotation(0, 0, delta);
                matrix.appendPosition(-localX, -localY);
                this.target.style.transform = matrix.getCss();
            }
        }

        this.onChangeAngle.dispatch(angle);
    }

    private isInRadius(): boolean {
        if (this.minRadius != 0 || this.maxRadius != Number.MAX_VALUE) {
            var center = this.center;
            var origin = this.getOrigin();
            // var radius = Point.distance(center, global);
            var radius = Point.distance(this._transform.toLocal(center.x, center.y), origin);
            if (radius < this.minRadius || radius > this.maxRadius) {
                return false;
            }
        }
        return true;
    }

    private calcAngle(): number {
        var center = this.center;
        this._transform.update(this.target);
        var origin = this.getOrigin();
        var global = this._transform.toGlobal(origin.x, origin.y, 0);
        var angle = Math.atan2(center.y - global.y, center.x - global.x) * 180 / Math.PI;
        angle %= 360;
        if (angle < 0)
            angle += 360;
        return angle;
    }

    private getOrigin(): IPoint {
        var origin: IPoint = null;
        if (this.origin === "auto") {
            origin = this._transform.origin;
        }
        else {
            origin = new Point();
            origin.x = Calc.relationCalced(this.origin.x, this._transform.element.offsetWidth, 0);
            origin.y = Calc.relationCalced(this.origin.y, this._transform.element.offsetHeight, 0);
        }
        return origin;
    }

    public get target(): HTMLElement {
        return this._target || this.element;
    }

    public set target(target: HTMLElement) {
        if (this._target === target)
            return;
        this._target = target;
        if (target && !this.element)
            this.element = target;
        this.refreshAngleByElement();
    }

    protected onChangeElement(element: HTMLElement): void {
        if (!this._target && element)
            this.refreshAngleByElement();
    }

    private refreshAngleByElement(): void {
        this._transform.update(this.target);
        var matrix = this._transform.matrix;
        this._angle = matrix.getRotation().z;
        // if (this.origin === "auto") {
        //     this._angle = matrix.getRotation().z;
        // }
        // else {
        //     var origin = this.getOrigin();
        //     var localX: number = origin.x - this._transform.origin.x;
        //     var localY: number = origin.y - this._transform.origin.y;
        //     matrix.appendPosition(localX, localY);
        //     this._angle = matrix.getRotation().z;
        // }
    }

    public dispose(): void {
        this._transform.dispose();
        this._transform = null;
        this.onSpin.dispose();
        this.onSpin = null;
        this.origin = null;
        this._target = null;
        super.dispose();
    }
}